import React, { useState, useMemo, useRef, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate } from 'react-router-dom';
import { FiSearch, FiPlus, FiEye, FiPlay, FiMoreVertical, FiEdit2, FiTrash2, FiCopy, FiX, FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import Hls from 'hls.js';
import { apiUrl } from '../../config/api';
import { VIDEO_LIST, VIDEO_DELETE, VIDEO_GET } from '../../api/contracts';
import './Page.css';
import './Category.css';

const COL_COUNT = 9;
//             Title, Channel, Status, Feed, Age, Content, Thumb, Video URL, Action
const MIN_WIDTHS_PX = [120,   90,     96,     80,   90,  90,      140,   88,        52];
const INITIAL_WIDTHS_PCT = [17, 9, 11, 7, 10, 10, 15, 10, 11];
const PAGE_SIZE = 50;
/** Request up to this many videos from the API (backend may cap at its own max). */
const VIDEO_LIST_LIMIT = 10000;

function truncateUrl(url, max = 24) {
  if (!url || typeof url !== 'string') return '—';
  return url.length > max ? url.slice(0, max) + '…' : url;
}

function formatList(arr) {
  if (!Array.isArray(arr) || arr.length === 0) return '—';
  return arr.length > 2 ? arr.slice(0, 2).join(', ') + '…' : arr.join(', ');
}

function Video() {
  const [videos, setVideos] = useState([]);
  const [allVideos, setAllVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState('');
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [videoPlayUrl, setVideoPlayUrl] = useState(null);
  const [actionMenuId, setActionMenuId] = useState(null);
  const [menuPosition, setMenuPosition] = useState(null);
  const [columnWidths, setColumnWidths] = useState(INITIAL_WIDTHS_PCT);
  const [resizeState, setResizeState] = useState({ index: null, startX: 0, startWidths: [] });
  const [copiedTitleId, setCopiedTitleId] = useState(null);
  const navigate = useNavigate();
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const tableRef = useRef(null);
  const videoRef = useRef(null);
  const hlsRef = useRef(null);

  const isHlsUrl = (url) => /\.m3u8(\?|$)/i.test(url || '');

  useEffect(() => {
    if (!videoPlayUrl || !videoRef.current) return;
    const video = videoRef.current;
    const url = videoPlayUrl;

    if (isHlsUrl(url)) {
      if (Hls.isSupported()) {
        const hls = new Hls();
        hlsRef.current = hls;
        hls.loadSource(url);
        hls.attachMedia(video);
        hls.on(Hls.Events.MANIFEST_PARSED, () => {
          video.play().catch(() => {});
        });
        return () => {
          hls.destroy();
          hlsRef.current = null;
        };
      }
      if (video.canPlayType('application/vnd.apple.mpegurl')) {
        video.src = url;
        video.play().catch(() => {});
        return () => { video.src = ''; };
      }
    } else {
      video.src = url;
      video.play().catch(() => {});
      return () => { video.src = ''; };
    }
  }, [videoPlayUrl]);

  const handleCopyTitle = async (text, rowId) => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopiedTitleId(rowId);
      setTimeout(() => setCopiedTitleId(null), 2000);
    } catch {
      setCopiedTitleId(null);
    }
  };

  const handleIdSearchSubmit = async (e) => {
    e.preventDefault();
    const term = search.trim();
    if (!term) {
      // Reset to full list when search is cleared and submitted
      setVideos(allVideos);
      setListError('');
      setCurrentPage(1);
      return;
    }

    setLoading(true);
    setListError('');
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await fetch(apiUrl(VIDEO_GET.pathFor(encodeURIComponent(term))), {
        method: VIDEO_GET.method,
        headers,
      });

      if (!res.ok) {
        if (res.status === 404) {
          setVideos([]);
          setCurrentPage(1);
          setListError('No video found with that ID.');
        } else {
          setListError(`Search failed (status ${res.status}).`);
        }
        return;
      }

      const json = await res.json().catch(() => ({}));
      const data = json.data || json;
      if (!data || typeof data !== 'object') {
        setVideos([]);
        setCurrentPage(1);
        setListError('No video found with that ID.');
        return;
      }

      setVideos([data]);
      setCurrentPage(1);
      setListError('');
    } catch (err) {
      setListError(err.message || 'Failed to search video by ID.');
    } finally {
      setLoading(false);
    }
  };

  const filtered = useMemo(() => {
    if (!search.trim()) return videos;
    const q = search.trim().toLowerCase();
    return videos.filter(
      (v) =>
        (v.id != null && String(v.id).toLowerCase().includes(q)) ||
        (v.video_id != null && String(v.video_id).toLowerCase().includes(q)) ||
        (v.title && v.title.toLowerCase().includes(q)) ||
        (v.channel_name && v.channel_name.toLowerCase().includes(q))
    );
  }, [videos, search]);

  const totalItems = filtered.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / PAGE_SIZE));
  const startIndex = (currentPage - 1) * PAGE_SIZE;
  const paginated = filtered.slice(startIndex, startIndex + PAGE_SIZE);
  const displayStart = totalItems === 0 ? 0 : startIndex + 1;
  const displayEnd = totalItems === 0 ? 0 : Math.min(startIndex + PAGE_SIZE, totalItems);

  useEffect(() => {
    setCurrentPage((p) => Math.min(p, totalPages));
  }, [totalPages, totalItems]);

  useEffect(() => {
    setCurrentPage(1);
  }, [search]);

  useLayoutEffect(() => {
    if (!actionMenuId || !triggerRef.current) {
      setMenuPosition(null);
      return;
    }
    const rect = triggerRef.current.getBoundingClientRect();
    setMenuPosition({ top: rect.bottom + 4, left: rect.left, width: rect.width });
  }, [actionMenuId]);

  useEffect(() => {
    function handleClickOutside(e) {
      if (actionMenuId && !triggerRef.current?.contains(e.target) && !menuRef.current?.contains(e.target)) {
        setActionMenuId(null);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [actionMenuId]);

  useEffect(() => {
    let cancelled = false;
    const token = localStorage.getItem('token');
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    fetch(apiUrl(`${VIDEO_LIST.path}?limit=${VIDEO_LIST_LIMIT}`), { method: VIDEO_LIST.method, headers })
      .then((res) => res.json().catch(() => ({})))
      .then((data) => {
        if (cancelled) return;
        const list = data.data || data;
        const safeList = Array.isArray(list) ? list : [];
        setVideos(safeList);
        setAllVideos(safeList);
        setListError(Array.isArray(list) ? '' : 'Invalid response from server.');
      })
      .catch((err) => {
        if (!cancelled) setListError(err.message || 'Failed to load videos.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  const handleEdit = (row) => {
    setActionMenuId(null);
    navigate(`/dashboard/cms/video/${row.id}/edit`);
  };

  const handleDuplicate = (row) => {
    setActionMenuId(null);
    navigate('/dashboard/cms/video/create', { state: { duplicateId: row.id } });
  };

  const handleRemove = async (row) => {
    setActionMenuId(null);
    const token = localStorage.getItem('token');
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    try {
      const res = await fetch(apiUrl(VIDEO_DELETE.pathFor(row.id)), {
        method: VIDEO_DELETE.method,
        headers,
      });
      if (res.ok) {
        setVideos((prev) => prev.filter((v) => v.id !== row.id));
        setAllVideos((prev) => prev.filter((v) => v.id !== row.id));
      }
    } catch {
      // keep list as is
    }
  };

  const handleResizeStart = (handleIndex, e) => {
    e.preventDefault();
    setResizeState({ index: handleIndex, startX: e.clientX, startWidths: [...columnWidths] });
  };

  useEffect(() => {
    if (resizeState.index === null) return;
    const table = tableRef.current;
    if (!table) return;
    const onMove = (e) => {
      const tw = table.offsetWidth;
      if (tw <= 0) return;
      const i = resizeState.index;
      const minLeftPct = (MIN_WIDTHS_PX[i] / tw) * 100;
      const minRightPct = (MIN_WIDTHS_PX[i + 1] / tw) * 100;
      const totalTwo = resizeState.startWidths[i] + resizeState.startWidths[i + 1];
      const deltaPct = ((e.clientX - resizeState.startX) / tw) * 100;
      let newLeft = resizeState.startWidths[i] + deltaPct;
      newLeft = Math.max(minLeftPct, Math.min(totalTwo - minRightPct, newLeft));
      const newRight = totalTwo - newLeft;
      setColumnWidths((prev) => {
        const next = [...prev];
        next[i] = newLeft;
        next[i + 1] = newRight;
        return next;
      });
    };
    const onUp = () => setResizeState((s) => ({ ...s, index: null }));
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    return () => {
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
  }, [resizeState.index, resizeState.startX, resizeState.startWidths]);

  const headers = ['Title', 'Channel', 'Status', 'Feed key', 'Age buckets', 'Content types', 'Thumbnail', 'Video URL', 'Action'];

  return (
    <div className="page category-page">
      <div className="category-header">
        <h3>Video</h3>
        <p className="category-subtitle">Manage videos</p>
      </div>

      <div className="category-toolbar">
        <form className="search-wrap" onSubmit={handleIdSearchSubmit}>
          <FiSearch className="search-icon" aria-hidden />
          <input
            type="text"
            className="search-input"
            placeholder="Search by video ID, title, or channel..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Search videos"
          />
        </form>
        <Link to="/dashboard/cms/video/create" className="btn-create">
          <FiPlus className="btn-icon" aria-hidden />
          Create Video
        </Link>
      </div>

      {loading && <p className="create-category-loading">Loading videos…</p>}
      {listError && <p className="create-category-error" role="alert">{listError}</p>}

      <div className="table-wrap">
        <table ref={tableRef} className="category-table category-table-resizable">
          <colgroup>
            {columnWidths.map((w, i) => (
              <col key={i} style={{ width: `${w}%`, minWidth: MIN_WIDTHS_PX[i] }} />
            ))}
          </colgroup>
          <thead>
            <tr>
              {headers.map((label, i) => (
                <th key={i} className={i === COL_COUNT - 1 ? 'th-action' : ''} style={{ width: `${columnWidths[i]}%` }}>
                  <span className="th-inner">{label}</span>
                  {i < COL_COUNT - 1 && (
                    <span
                      className="resize-handle"
                      onMouseDown={(e) => handleResizeStart(i, e)}
                      role="separator"
                      aria-orientation="vertical"
                      aria-label={`Resize column ${i + 1}`}
                    />
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {paginated.length === 0 ? (
              <tr>
                <td colSpan={COL_COUNT} className="table-empty">
                  No videos found.
                </td>
              </tr>
            ) : (
              paginated.map((row) => (
                <tr key={row.id}>
                  <td className="cell-name">
                    <span className="cell-title-wrap">
                      <span className="cell-ellipsis cell-title-text" title={row.title || ''}>{row.title || '—'}</span>
                      {row.title && (
                        <button
                          type="button"
                          className="video-title-copy-btn"
                          onClick={() => handleCopyTitle(row.title, row.id)}
                          title="Copy video title"
                          aria-label="Copy video title"
                        >
                          {copiedTitleId === row.id ? (
                            <span className="video-title-copied">Copied!</span>
                          ) : (
                            <FiCopy className="video-title-copy-icon" aria-hidden />
                          )}
                        </button>
                      )}
                    </span>
                  </td>
                  <td>
                    <span className="cell-ellipsis" title={row.channel_name || ''}>{row.channel_name || '—'}</span>
                  </td>
                  <td>
                    <span className={`status-pill status-${(row.status || '').toLowerCase()}`}>
                      {row.status || '—'}
                    </span>
                  </td>
                  <td>
                    <span className="cell-ellipsis" title={row.feed_key || ''}>{row.feed_key || '—'}</span>
                  </td>
                  <td>
                    <span className="cell-ellipsis" title={(row.age_buckets || []).map((v) => (v === '<3' ? '0-3' : v)).join(', ')}>{formatList((row.age_buckets || []).map((v) => (v === '<3' ? '0-3' : v)))}</span>
                  </td>
                  <td>
                    <span className="cell-ellipsis" title={(row.content_types || []).join(', ')}>{formatList(row.content_types)}</span>
                  </td>
                  <td>
                    <div className="cell-thumbnail">
                      <span className="thumbnail-url" title={row.thumbnail_url}>
                        {truncateUrl(row.thumbnail_url)}
                      </span>
                      {row.thumbnail_url && (
                        <button
                          type="button"
                          className="btn-watch"
                          onClick={() => setPreviewUrl(row.thumbnail_url)}
                          title="View thumbnail"
                          aria-label="View thumbnail"
                        >
                          <FiEye className="watch-icon" />
                        </button>
                      )}
                    </div>
                  </td>
                  <td>
                    <div className="cell-video-url">
                      {row.video_url ? (
                        <button
                          type="button"
                          className="btn-play-video"
                          onClick={() => setVideoPlayUrl(row.video_url)}
                          title={row.video_url}
                          aria-label="Play video"
                        >
                          <FiPlay className="btn-play-icon" />
                          Play
                        </button>
                      ) : (
                        <span className="cell-ellipsis">—</span>
                      )}
                    </div>
                  </td>
                  <td className="cell-action">
                    <div className="action-wrap">
                      <button
                        ref={actionMenuId === row.id ? triggerRef : null}
                        type="button"
                        className="btn-dots"
                        onClick={() => setActionMenuId(actionMenuId === row.id ? null : row.id)}
                        aria-label="Open menu"
                        aria-expanded={actionMenuId === row.id}
                      >
                        <FiMoreVertical className="dots-icon" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {totalItems > 0 && (
        <div className="pagination-wrap">
          <button
            type="button"
            className="pagination-btn"
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            aria-label="Previous page"
          >
            <FiChevronLeft className="pagination-arrow" aria-hidden />
          </button>
          <span className="pagination-text">
            Showing {displayStart}–{displayEnd} of {totalItems}
          </span>
          <button
            type="button"
            className="pagination-btn"
            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            disabled={currentPage >= totalPages}
            aria-label="Next page"
          >
            <FiChevronRight className="pagination-arrow" aria-hidden />
          </button>
        </div>
      )}

      {actionMenuId && menuPosition && (() => {
        const row = filtered.find((v) => v.id === actionMenuId);
        if (!row) return null;
        return createPortal(
          <div
            ref={menuRef}
            className="action-menu action-menu-portal"
            style={{ position: 'fixed', top: menuPosition.top, left: menuPosition.left, zIndex: 1000 }}
          >
            <button type="button" className="action-menu-item" onClick={() => handleEdit(row)}>
              <FiEdit2 className="action-menu-icon" />
              Edit video
            </button>
            <button type="button" className="action-menu-item" onClick={() => handleDuplicate(row)}>
              <FiCopy className="action-menu-icon" />
              Duplicate video
            </button>
            <button
              type="button"
              className="action-menu-item"
              disabled={(row.status || '').toLowerCase() !== 'inactive'}
              onClick={() => (row.status || '').toLowerCase() === 'inactive' && handleRemove(row)}
              title={(row.status || '').toLowerCase() !== 'inactive' ? 'Only inactive videos can be removed' : 'Remove video'}
            >
              <FiTrash2 className="action-menu-icon" />
              Remove video
            </button>
          </div>,
          document.body
        );
      })()}

      {previewUrl && (
        <div className="thumbnail-overlay" onClick={() => setPreviewUrl(null)} role="dialog" aria-modal="true" aria-label="Thumbnail preview">
          <div className="thumbnail-modal" onClick={(e) => e.stopPropagation()}>
            <button type="button" className="btn-close-preview" onClick={() => setPreviewUrl(null)} aria-label="Close preview">
              <FiX className="close-icon" />
            </button>
            <img src={previewUrl} alt="Video thumbnail" className="thumbnail-preview-img" />
          </div>
        </div>
      )}

      {videoPlayUrl && (
        <div
          className="video-player-overlay"
          onClick={() => setVideoPlayUrl(null)}
          role="dialog"
          aria-modal="true"
          aria-label="Video player"
        >
          <div className="video-player-modal" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className="btn-close-preview"
              onClick={() => setVideoPlayUrl(null)}
              aria-label="Close video"
            >
              <FiX className="close-icon" />
            </button>
            <video
              ref={videoRef}
              className="video-player-element"
              controls
              playsInline
              aria-label="Video playback"
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default Video;
